import { useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { formatDateTime } from "../../../shared/time";
import type { Assignment, Person, PlanDetail, PlanPosition } from "../../../shared/types";
import { Badge, ErrorNote } from "../../ui";
import { assignPerson, listPeople, removeAssignment, respondToAssignment } from "./client";
import { activeFirst, messageOf, peopleForPosition, statusTone } from "./model";

export function TeamBoard({ plan, canEdit, personId, onPlan }: {
  plan: PlanDetail;
  canEdit: boolean;
  personId: string | null;
  onPlan: (plan: PlanDetail) => void;
}) {
  const [query, setQuery] = useState("");
  const [people, setPeople] = useState<Person[]>([]);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancel = false;
    const handle = window.setTimeout(() => {
      void listPeople(query)
        .then((rows) => { if (!cancel) setPeople(activeFirst(rows)); })
        .catch((caught: unknown) => { if (!cancel) setError(messageOf(caught)); });
    }, 160);
    return () => { cancel = true; window.clearTimeout(handle); };
  }, [query]);

  return (
    <div className="stack">
      <div>
        <h2>Team</h2>
        <p className="muted">Positions come from the service type. Asking someone notifies them inside MB Live. Email and SMS are not sent.</p>
      </div>
      {canEdit && (
        <label className="field svc-search">Find a person
          <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Name" />
        </label>
      )}
      {error && <ErrorNote>{error}</ErrorNote>}
      {plan.teams.length === 0 && <p className="muted">This plan has no positions. Teams are copied from the service type when a plan is created.</p>}
      <div className="svc-teams">
        {plan.teams.map((team) => (
          <article className="panel panel-pad svc-team stack" key={team.id}>
            <h3>{team.name}</h3>
            {team.positions.map((position) => (
              <PositionCard key={position.id} position={position} people={people} canEdit={canEdit} personId={personId} onPlan={onPlan} />
            ))}
            {team.positions.length === 0 && <p className="muted">This team has no positions to fill.</p>}
          </article>
        ))}
      </div>
    </div>
  );
}

function PositionCard({ position, people, canEdit, personId, onPlan }: {
  position: PlanPosition;
  people: Person[];
  canEdit: boolean;
  personId: string | null;
  onPlan: (plan: PlanDetail) => void;
}) {
  const taken = new Set(position.assignments.map((assignment) => assignment.personId));
  const available = people.filter((person) => !taken.has(person.id));
  const grouped = peopleForPosition(available, position.name);
  const open = !position.assignments.some((assignment) => assignment.status !== "declined");
  const [chosen, setChosen] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function assign(event: FormEvent) {
    event.preventDefault();
    if (!chosen) return;
    setBusy(true);
    setError("");
    try {
      onPlan(await assignPerson(position.id, chosen));
      setChosen("");
    } catch (caught) {
      setError(messageOf(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="svc-position">
      <div className="split">
        <h4>{position.name}</h4>
        {open && <Badge tone="amber">Open</Badge>}
      </div>
      {position.assignments.map((assignment) => (
        <AssignmentCard
          key={assignment.id}
          assignment={assignment}
          canEdit={canEdit}
          canRespond={canEdit || personId === assignment.personId}
          mine={personId === assignment.personId}
          onPlan={onPlan}
        />
      ))}
      {error && <ErrorNote>{error}</ErrorNote>}
      {canEdit && (
        <form className="cluster" onSubmit={(event) => void assign(event)}>
          <select id={`ask-${position.id}`} value={chosen} onChange={(event) => setChosen(event.target.value)} aria-label={`Ask someone for ${position.name}`}>
            <option value="">Ask someone</option>
            {grouped.suggested.length > 0 && <optgroup label="Likely fit">{grouped.suggested.map((person) => <PersonOption key={person.id} person={person} />)}</optgroup>}
            {grouped.others.length > 0 && <optgroup label={grouped.suggested.length > 0 ? "Everyone else" : "People"}>{grouped.others.map((person) => <PersonOption key={person.id} person={person} />)}</optgroup>}
          </select>
          <button className="btn small" type="submit" disabled={busy || !chosen}>{busy ? "Asking…" : "Ask"}</button>
        </form>
      )}
    </section>
  );
}

function PersonOption({ person }: { person: Person }) {
  const instruments = person.instruments.length > 0 ? ` · ${person.instruments.join(", ")}` : "";
  return <option value={person.id}>{person.name}{instruments}</option>;
}

function AssignmentCard({ assignment, canEdit, canRespond, mine, onPlan }: {
  assignment: Assignment;
  canEdit: boolean;
  canRespond: boolean;
  mine: boolean;
  onPlan: (plan: PlanDetail) => void;
}) {
  const [note, setNote] = useState(assignment.responseNote);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    setNote(assignment.responseNote);
  }, [assignment.id, assignment.responseNote, assignment.status]);

  async function respond(status: Assignment["status"]) {
    setBusy(true);
    setError("");
    try {
      onPlan(await respondToAssignment(assignment.id, status, note.trim()));
    } catch (caught) {
      setError(messageOf(caught));
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    setBusy(true);
    setError("");
    try {
      onPlan(await removeAssignment(assignment.id));
    } catch (caught) {
      setError(messageOf(caught));
      setBusy(false);
    }
  }

  return (
    <article className={mine ? "svc-assignment mine" : "svc-assignment"}>
      <div className="split">
        <Link to={`/people/${assignment.personId}`}><strong>{assignment.personName}</strong></Link>
        <span className="cluster">
          {mine && <Badge tone="teal">You</Badge>}
          <Badge tone={statusTone(assignment.status)}>{assignment.status}</Badge>
        </span>
      </div>
      {assignment.respondedAt && <span className="faint">{formatDateTime(assignment.respondedAt)}</span>}
      {assignment.conflicts.length > 0 && (
        <ul className="svc-conflicts">
          {assignment.conflicts.map((conflict, index) => <li key={`${assignment.id}-${index}`}>{conflict}</li>)}
        </ul>
      )}
      {error && <ErrorNote>{error}</ErrorNote>}
      {canRespond ? (
        <>
          <label className="field" htmlFor={`note-${assignment.id}`}>{mine ? "Your note" : `Note for ${assignment.personName}`}
            <textarea id={`note-${assignment.id}`} maxLength={500} value={note} onChange={(event) => setNote(event.target.value)} />
          </label>
          <div className="cluster">
            <button className="btn small teal" type="button" disabled={busy} onClick={() => void respond("accepted")}>Accept</button>
            <button className="btn small danger" type="button" disabled={busy} onClick={() => void respond("declined")}>Decline</button>
            <button className="btn small ghost" type="button" disabled={busy} onClick={() => void respond("pending")}>Mark pending</button>
          </div>
        </>
      ) : (
        assignment.responseNote && <p className="muted svc-prose">{assignment.responseNote}</p>
      )}
      {canEdit && (confirming ? (
        <div className="cluster">
          <span>Remove {assignment.personName}?</span>
          <button className="btn small danger" type="button" disabled={busy} onClick={() => void remove()}>Remove</button>
          <button className="btn small ghost" type="button" onClick={() => setConfirming(false)}>Keep</button>
        </div>
      ) : (
        <button className="btn small ghost" type="button" onClick={() => setConfirming(true)}>Remove</button>
      ))}
    </article>
  );
}
